// --- Dependencies -------------------------------------------------------------------------------
import { useEffect } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import constants from "@/config/constants";
import useFetch from "@/hooks/useFetch";
import usePopup from "@/hooks/usePopup";
import useTranslation from "@/hooks/useTranslation";
import type { TyWarranty, TyWarrantyRecord } from "@/types";
import WarrantyCard from "@/UI/cards/WarrantyCard";
import EmptyState from "@/UI/components/EmptyState";
import InteractionFormPopup from "@/UI/popups/InteractionFormPopup";
import WarrantyPopup from "@/UI/popups/WarrantyPopup";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
type Props = {
    /**
     * Which section's routes to call. The same three endpoints are mounted under `properties` and
     * under `sales`, and a screen calls its own section's — see the warranty plan, §5.
     */
    basePath: "/properties" | "/sales";
    /** One or the other, never both — this is the unit's page or the customer's. */
    unitId?: string;
    customerId?: string;
    /**
     * The unit's own `warranties[]`, shown while no sale has turned them into records. A unit always
     * shows what it is covered by; before the deal that is a list with no clock running on it.
     */
    templates?: TyWarranty[];
    /** Editing templates belongs to the unit, and only while it is unsold. */
    onAddTemplate?: () => void;
    onEditTemplate?: (warranty: TyWarranty) => void;
    onRemoveTemplate?: (warranty: TyWarranty) => void;
    /** Set by the customer screen's filter bar. */
    unitFilter?: string;
    stateFilter?: string;
    /** Bumped by the screen when something it owns changed, so the list reloads with it. */
    reloadAt?: number | null;
};

/**
 * Every guarantee on one unit, or every guarantee one person holds.
 *
 * **One component, two screens**, for the same reason `RecordDeals` is one: a warranty belongs to
 * the unit it covers and to the buyer who holds it equally, so neither of them owns the list. What
 * differs is which column is already known and therefore not printed twice, and what may be edited
 * — templates are the unit's business and never the customer's.
 *
 * Expired rows are listed rather than hidden. "Was this covered when it broke" is the question
 * somebody opens this tab with, and a list of only what is live cannot answer it.
 *
 * **Every action is in the popup**, including editing a template and following a record up. The
 * cards are a list somebody scans to find the one that needs them; four buttons on each row would
 * be offering answers before the question has been picked.
 */
export default function RecordWarranties({
    basePath,
    unitId,
    customerId,
    templates,
    onAddTemplate,
    onEditTemplate,
    onRemoveTemplate,
    unitFilter,
    stateFilter,
    reloadAt,
}: Props) {
    const t = useTranslation();
    const addComp = usePopup((state) => state.addComp);

    const warranties = useFetch<{ warranties: TyWarrantyRecord[] }>(`${basePath}/warranties`, {}, false);

    const load = () => {
        const query: Record<string, unknown> = { limit: constants.maxPageSize };
        if (unitId) query.unit = unitId;
        if (customerId) query.customer = customerId;
        if (unitFilter) query.unit = unitFilter;
        if (stateFilter) query.state = stateFilter;
        warranties.invoke({ query });
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [unitId, customerId, unitFilter, stateFilter, reloadAt]);

    const rows = warranties.data?.warranties ?? [];
    /** The page it is mounted on, which decides which half of the pair each card prints. */
    const place = unitId ? "unit" : "customer";

    /** Following one up is writing down who was called about it — see §7 of the warranty plan. */
    const openContact = (warranty: TyWarrantyRecord) =>
        addComp({
            name: "interaction-form",
            Component: InteractionFormPopup,
            anim: "scale",
            details: {
                warrantyId: warranty._id,
                customerId: typeof warranty.customer === "object" && warranty.customer ? warranty.customer._id : String(warranty.customer),
                onDone: load,
            },
        });

    /** The card's one gesture. Everything it is too small to carry is in here, actions included. */
    const openRecord = (warranty: TyWarrantyRecord) =>
        addComp({
            name: "warranty",
            Component: WarrantyPopup,
            anim: "scale",
            details: { warranty: { kind: "record", ...warranty }, onContact: () => openContact(warranty) },
        });

    const openTemplate = (warranty: TyWarranty) =>
        addComp({
            name: "warranty",
            Component: WarrantyPopup,
            anim: "scale",
            details: {
                warranty: { kind: "template", ...warranty },
                onEdit: onEditTemplate ? () => onEditTemplate(warranty) : undefined,
                onRemove: onRemoveTemplate ? () => onRemoveTemplate(warranty) : undefined,
            },
        });

    // Before any sale there are no records, so the unit shows what it carries instead.
    const shows = templates ?? [];
    const isShowingTemplates = !rows.length && shows.length > 0;

    if (warranties.loading && !rows.length && !shows.length) return <p className="record-warranties__state">{t.common.loading}</p>;

    if (!rows.length && !shows.length) {
        const forUnit = !!unitId;
        return (
            <EmptyState
                icon="crown"
                title={forUnit ? t.warranties.emptyTemplates : t.warranties.emptyCustomer}
                // "Add them before it is sold" is no help on a unit that already is — and on a sold
                // one with nothing covered, the bare sentence is the whole truth anyway.
                description={forUnit ? (onAddTemplate ? t.warranties.emptyTemplatesHint : undefined) : t.warranties.emptyCustomerHint}
                actionLabel={onAddTemplate ? t.properties.addWarranty : undefined}
                onAction={onAddTemplate}
            />
        );
    }

    return (
        <div className="record-warranties">
            {warranties.error && <p className="record-warranties__state record-warranties__state--error">{warranties.error}</p>}

            {isShowingTemplates && <p className="record-warranties__note">{t.warranties.notStartedHint}</p>}

            <div className="record-warranties__grid">
                {isShowingTemplates
                    ? shows.map((warranty) => (
                          <WarrantyCard
                              key={warranty._id}
                              warranty={{ kind: "template", ...warranty }}
                              place={place}
                              onOpen={() => openTemplate(warranty)}
                          />
                      ))
                    : rows.map((warranty) => (
                          <WarrantyCard
                              key={warranty._id}
                              warranty={{ kind: "record", ...warranty }}
                              place={place}
                              onOpen={() => openRecord(warranty)}
                          />
                      ))}
            </div>
        </div>
    );
}
